import React, { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { Card } from '../ui/card';
import { 
  Plus, Search, Edit, Trash2, ChevronLeft, ChevronRight, 
  Car, Home, AlertCircle, Filter, MoreHorizontal 
} from 'lucide-react';
import useParking from '../../api/parking';
import useUser from '../../api/user';
import ParkingFormModal from './modals/ParkingFormModal';
import { Alert, AlertTitle, AlertDescription } from '../ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { 
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem
} from '../ui/select';

const ParkingManagement = ({ searchQuery: initialSearchQuery = '' }) => {
    const { t } = useTranslation();
    const [searchQuery, setSearchQuery] = useState(initialSearchQuery);
    const [filteredSpots, setFilteredSpots] = useState([]);
    const parkingApi = useParking();
    const userApi = useUser();
    const [spots, setSpots] = useState([]);
    const [users, setUsers] = useState([]);
    const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
    const [showFilters, setShowFilters] = useState(false);

    // Track current filter selections
    const [typeFilter, setTypeFilter] = useState("all");
    const [statusFilter, setStatusFilter] = useState("all");
    const [floorFilter, setFloorFilter] = useState("all");

    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingSpot, setEditingSpot] = useState(null);
    
    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const [spotsPerPage, setSpotsPerPage] = useState(10);

    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [selectedSpotId, setSelectedSpotId] = useState(null);
    const [selectedSpot, setSelectedSpot] = useState(null);

    const [alertInfo, setAlertInfo] = useState({
        show: false,
        message: '',
        variant: 'info',
        timeoutId: null
    });

    // Check for mobile view
    useEffect(() => {
      const handleResize = () => {
        const mobile = window.innerWidth < 768;
        setIsMobileView(mobile);
        // Set fewer items per page on mobile
        setSpotsPerPage(mobile ? 5 : 10);
      };
      
      handleResize(); // Set initial values
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }, []);

    const showAlert = (message, variant) => {
        if (alertInfo.timeoutId) {
            clearTimeout(alertInfo.timeoutId);
        }
        
        const timeoutId = setTimeout(() => {
            hideAlert();
        }, 3000); // 3 seconds
        
        setAlertInfo({ show: true, message, variant, timeoutId });
    };

    const hideAlert = () => {
        if (alertInfo.timeoutId) {
            clearTimeout(alertInfo.timeoutId);
        }
        
        setAlertInfo({ ...alertInfo, show: false, timeoutId: null });
    };

    useEffect(() => {
      return () => {
        if (alertInfo.timeoutId) {
            clearTimeout(alertInfo.timeoutId);
        }
      };
    }, [alertInfo.timeoutId]);

    useEffect(() => {
      fetchSpots();
      fetchUsers();
    }, []);

    // Reset to first page when filters change
    useEffect(() => {
      setCurrentPage(1);
      applyAllFilters();
    }, [searchQuery, typeFilter, statusFilter, floorFilter, spots]);

    const fetchSpots = async () => {
      try {
        const data = await parkingApi.getAllParkingSpots();
        setSpots(data.spots);
        setFilteredSpots(data.spots);
      } catch (error) {
        console.error(t('management.parking.errors.fetchSpots'), error);
        showAlert(t('management.parking.alerts.loadError'), "destructive");
      }
    };

    const fetchUsers = async () => {
      try {
        const data = await userApi.getAllUsers();
        setUsers(data);
      } catch (error) {
        console.error(t('management.parking.errors.fetchUsers'), error);
      }
    };

    const applyAllFilters = () => {
      let filtered = spots.filter(spot => {
        // Apply type filter
        const typeMatches = 
            typeFilter === "all" || 
            spot.type?.toLowerCase() === typeFilter.toLowerCase();
        
        // Apply status filter
        const statusMatches = 
            statusFilter === "all" || 
            (statusFilter === "assigned" && spot.owner_id) ||
            (statusFilter === "unassigned" && !spot.owner_id) ||
            (spot.status?.toLowerCase() === statusFilter.toLowerCase());
        
        // Apply floor filter
        const floorMatches = 
            floorFilter === "all" || 
            spot.floor?.toString() === floorFilter;
        
        // Apply search filter if query exists
        const searchMatches = !searchQuery || 
            spot.spot_number?.toLowerCase().includes(searchQuery.toLowerCase());
        
        // All conditions must be true
        return typeMatches && statusMatches && floorMatches && searchMatches;
      });
    
      setFilteredSpots(filtered);
    };
  
    // Update filter handlers
    const handleTypeFilterChange = (value) => {
      setTypeFilter(value);
    };

    const handleStatusFilterChange = (value) => {
      setStatusFilter(value);
    };

    const handleFloorFilterChange = (value) => {
      setFloorFilter(value);
    };

    // Add a new parking spot
    const handleAddSpot = async (spotData) => {
      try {
        const newSpot = await parkingApi.addParkingSpot(spotData);
        setSpots([...spots, newSpot]);
        showAlert(t('management.parking.alerts.addSuccess'), "success");
        return newSpot;
      } catch (error) {
        console.error(t('management.parking.errors.addSpot'), error);
        showAlert(t('management.parking.alerts.addError'), "destructive");
        throw error;
      }
    };

    // Update a parking spot
    const handleUpdateSpot = async (spotData) => {
      try {
        const updatedSpot = await parkingApi.updateParkingSpot(spotData);
        setSpots(spots.map(spot => 
          spot.id === updatedSpot.id ? updatedSpot : spot
        ));
        showAlert(t('management.parking.alerts.updateSuccess'), "success");
        return updatedSpot;
      } catch (error) {
        console.error(t('management.parking.errors.updateSpot'), error);
        showAlert(t('management.parking.alerts.updateError'), "destructive");
        throw error;
      }
    };

    const handleDeleteSpot = (spotId, spot) => {
        setSelectedSpotId(spotId);
        setSelectedSpot(spot);
        setDeleteModalOpen(true);
    };

    const confirmDelete = async () => {
      try {
        await parkingApi.deleteParkingSpot(selectedSpotId);
        // Remove the deleted spot from state
        setSpots(spots.filter(spot => spot.id !== selectedSpotId));
        // Show success alert
        showAlert(t('management.parking.alerts.deleteSuccess'), "success");
        setDeleteModalOpen(false);
      } catch (error) {
        console.error(t('management.parking.errors.deleteSpot'), error);
        // Show error alert
        showAlert(t('management.parking.alerts.deleteError'), "destructive");
      }
    };

    // Function to handle modal submission (either add or update)
    const handleSubmitSpot = (spotData) => {
      if (editingSpot) {
        return handleUpdateSpot(spotData);
      } else {
        return handleAddSpot(spotData);
      }
    };
    
    // Function to open modal for editing
    const openEditModal = (spot) => {
      setEditingSpot(spot);
      setIsModalOpen(true);
    };

    // Function to open modal for adding
    const openAddModal = () => {
      setEditingSpot(null);
      setIsModalOpen(true);
    };

    // Function to close modal
    const closeModal = () => {
      setIsModalOpen(false);
      setEditingSpot(null);
    };
    
    // Get owner username from owner_id
    const getOwnerUsername = (ownerId) => {
      if (!ownerId) return t('management.parking.noOwner');
      const owner = users.find(user => user.id === ownerId);
      return owner ? owner.username : t('management.parking.unknownOwner');
    };
    
    // Get current spots for pagination
    const indexOfLastSpot = currentPage * spotsPerPage;
    const indexOfFirstSpot = indexOfLastSpot - spotsPerPage;
    const currentSpots = filteredSpots.slice(indexOfFirstSpot, indexOfLastSpot);
    
    // Calculate total pages
    const totalPages = Math.ceil(filteredSpots.length / spotsPerPage);
    
    // Change page
    const nextPage = () => {
      if (currentPage < totalPages) {
        setCurrentPage(currentPage + 1);
      }
    };
    
    const prevPage = () => {
      if (currentPage > 1) {
        setCurrentPage(currentPage - 1);
      }
    };
    
    // Go to specific page
    const goToPage = (pageNumber) => {
      setCurrentPage(pageNumber);
    };
    
    // Generate page numbers - limit visible pages on mobile
    const getPageNumbers = () => {
      if (isMobileView) {
        // On mobile, show fewer page numbers
        if (totalPages <= 3) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
          // Show current page and one on each side when possible
          if (currentPage === 1) {
            return [1, 2, 3];
          } else if (currentPage === totalPages) {
            return [totalPages - 2, totalPages - 1, totalPages];
          } else {
            return [currentPage - 1, currentPage, currentPage + 1];
          }
        }
      } else {
        // On desktop, show all pages (or up to 5)
        if (totalPages <= 5) {
          return Array.from({ length: totalPages }, (_, i) => i + 1);
        } else {
          // More than 5 pages, show current page and 2 on each side when possible
          const startPage = Math.max(1, currentPage - 2);
          const endPage = Math.min(totalPages, startPage + 4);
          return Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i);
        }
      }
    };

    const pageNumbers = getPageNumbers();

    // Mobile card view component
    const SpotCard = ({ spot }) => (
      <Card className="mb-3 overflow-hidden">
        <div className="p-4">
          <div className="flex justify-between items-start mb-2">
            <div>
              <h3 className="font-semibold">{spot.spot_number}</h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  spot.type?.toLowerCase() === 'garage' 
                  ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                }`}>
                  {spot.type?.toLowerCase() === 'garage' ? <Home className="w-3 h-3" /> : <Car className="w-3 h-3" />}
                  {spot.type?.toLowerCase() === 'garage' 
                    ? t('management.parking.types.garage') 
                    : t('management.parking.types.spot')
                  }
                </span>
                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  spot.status === 'available' 
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                  : spot.status === 'occupied'
                    ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                    : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                }`}>
                  {spot.status 
                    ? t(`management.parking.status.${spot.status}`)
                    : t('management.parking.status.notSet')
                  }
                </span>
              </div>
            </div>
            
            <DropdownMenu>
              <DropdownMenuTrigger className="p-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-800">
                <MoreHorizontal className="h-5 w-5 text-neutral-500" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => openEditModal(spot)}>
                  <Edit className="w-4 h-4 mr-2" /> {t('management.parking.actions.edit')}
                </DropdownMenuItem>
                <DropdownMenuItem 
                  onClick={() => handleDeleteSpot(spot.id, spot)} 
                  className="text-red-600 dark:text-red-400"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> {t('management.parking.actions.delete')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <div className="grid grid-cols-2 gap-2 mt-3 text-sm">
            <div>
              <p className="text-neutral-500 dark:text-neutral-400">{t('management.parking.table.floor')}</p>
              <p className="font-medium">{spot.floor || t('management.parking.noOwner')}</p>
            </div>
            <div>
              <p className="text-neutral-500 dark:text-neutral-400">{t('management.parking.table.owner')}</p>
              <p className="font-medium truncate">{getOwnerUsername(spot.owner_id)}</p>
            </div>
          </div>
        </div>
      </Card>
    );

    const DeleteConfirmModal = ({ isOpen, onClose, onConfirm }) => {
      if (!isOpen) return null;

      return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-neutral-800 p-5 rounded-lg max-w-md w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-500 w-6 h-6 flex-shrink-0" />
              <h3 className="text-lg font-semibold">{t('management.parking.deleteModal.title')}</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              {t('management.parking.deleteModal.description', {
                spotNumber: selectedSpot?.spot_number || t('management.parking.noOwner')
              })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-md hover:bg-neutral-100 dark:hover:bg-neutral-700"
              >
                {t('management.parking.deleteModal.cancel')}
              </button>
              <button
                onClick={onConfirm}
                className="px-4 py-2 bg-red-500 text-white rounded-md hover:bg-red-600"
              >
                {t('management.parking.deleteModal.confirm')}
              </button>
            </div>
          </div>
        </div>
      );
    };
    
    return (
      <div className="space-y-4 md:space-y-6 p-2 md:p-4">
        {/* Header with Add Button */}
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">{t('management.parking.title')}</h2>
            <p className="text-sm text-muted-foreground hidden md:block">{t('management.parking.description')}</p>
          </div>
          <button 
            className="flex items-center gap-1 md:gap-2 bg-primary hover:bg-primary/90 text-white px-3 py-1.5 md:px-4 md:py-2 rounded-lg transition-colors text-sm md:text-base"
            onClick={openAddModal}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden md:inline">{t('management.parking.addSpot')}</span>
            <span className="md:hidden">{t('management.parking.add')}</span>
          </button>
        </div>

        {/* Search and Filter Toggle for Mobile */}
        <div className="flex gap-2 md:hidden">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
            <input
              type="text"
              placeholder={t('management.parking.searchPlaceholder')}
              className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all text-sm"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button 
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg ${showFilters ? 'bg-primary text-white' : 'bg-neutral-100 dark:bg-neutral-800'}`}
          >
            <Filter className="w-5 h-5" />
          </button>
        </div>

        {/* Expandable Filters on Mobile */}
        {(showFilters || !isMobileView) && (
          <Card className={`p-4 bg-white dark:bg-neutral-900 ${isMobileView ? 'animate-in slide-in-from-top duration-300' : ''}`}>
            <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-4">
              {/* Search - Only visible on desktop */}
              <div className="relative flex-1 hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                <input
                  type="text"
                  placeholder={t('management.parking.searchPlaceholderDetailed')}
                  className="pl-10 pr-4 py-2 w-full rounded-lg bg-neutral-100 dark:bg-neutral-800 border border-transparent focus:border-primary focus:ring-2 focus:ring-primary/20 transition-all"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              
              {/* Type Filter */}
              <div className="w-full md:w-48">
                <Select
                  value={typeFilter}
                  onValueChange={handleTypeFilterChange}
                >
                  <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                    <SelectValue placeholder={t('management.parking.filters.allTypes')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('management.parking.filters.allTypes')}</SelectItem>
                    <SelectItem value="spot">{t('management.parking.types.spot')}</SelectItem>
                    <SelectItem value="garage">{t('management.parking.types.garage')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div className="w-full md:w-48">
                <Select
                  value={statusFilter}
                  onValueChange={handleStatusFilterChange}
                >
                  <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                    <SelectValue placeholder={t('management.parking.filters.allStatus')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('management.parking.filters.allStatus')}</SelectItem>
                    <SelectItem value="available">{t('management.parking.status.available')}</SelectItem>
                    <SelectItem value="occupied">{t('management.parking.status.occupied')}</SelectItem>
                    <SelectItem value="assigned">{t('management.parking.status.assigned')}</SelectItem>
                    <SelectItem value="unassigned">{t('management.parking.status.unassigned')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Floor Filter */}
              <div className="w-full md:w-48">
                <Select
                  value={floorFilter}
                  onValueChange={handleFloorFilterChange}
                >
                  <SelectTrigger className="bg-neutral-100 dark:bg-neutral-800 w-full">
                    <SelectValue placeholder={t('management.parking.filters.allFloors')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('management.parking.filters.allFloors')}</SelectItem>
                    <SelectItem value="1">{t('management.parking.filters.floor', { number: 1 })}</SelectItem>
                    <SelectItem value="2">{t('management.parking.filters.floor', { number: 2 })}</SelectItem>
                    <SelectItem value="3">{t('management.parking.filters.floor', { number: 3 })}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </Card>
        )}

        {/* Mobile Card View */}
        {isMobileView ? (
          <div className="space-y-1">
            {currentSpots.length > 0 ? (
              currentSpots.map(spot => (
                <SpotCard key={`mobile-${spot.id}`} spot={spot} />
              ))
            ) : (
              <Card className="p-8 text-center">
                <p className="text-muted-foreground">{t('management.parking.noSpots')}</p>
              </Card>
            )}
          </div>
        ) : (
          /* Desktop Table View */
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.spotNumber')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.type')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.floor')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.status')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.owner')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">{t('management.parking.table.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-200 dark:divide-neutral-700">
                  {currentSpots.length > 0 ? (
                    currentSpots.map(spot => (
                      <tr key={spot.id} className="hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap font-medium">{spot.spot_number}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            spot.type?.toLowerCase() === 'garage' 
                            ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' 
                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                          }`}>
                            {spot.type?.toLowerCase() === 'garage' ? <Home className="w-3 h-3" /> : <Car className="w-3 h-3" />}
                            {spot.type?.toLowerCase() === 'garage' 
                              ? t('management.parking.types.garage') 
                              : t('management.parking.types.spot')
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{spot.floor || t('management.parking.noOwner')}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                            spot.status === 'available' 
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' 
                            : spot.status === 'occupied'
                              ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                              : 'bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400'
                          }`}>
                            {spot.status 
                              ? t(`management.parking.status.${spot.status}`)
                              : t('management.parking.status.notSet')
                            }
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">{getOwnerUsername(spot.owner_id)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            <button 
                              className="flex items-center gap-1 bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 px-2 py-1 rounded hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors"
                              onClick={() => openEditModal(spot)}
                            >
                              <Edit className="w-3 h-3" />
                              {t('management.parking.actions.edit')}
                            </button>
                            <button 
                              className="flex items-center gap-1 bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400 px-2 py-1 rounded hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                              onClick={() => handleDeleteSpot(spot.id, spot)}
                            >
                              <Trash2 className="w-3 h-3" />
                              {t('management.parking.actions.delete')}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-muted-foreground">
                        {t('management.parking.noMatchingSpots')}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        )}
        
        {/* Pagination Controls - Responsive */}
        {filteredSpots.length > 0 && (
          <Card className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
              <div className="text-xs md:text-sm text-neutral-500 dark:text-neutral-400 hidden sm:block">
                {t('management.parking.pagination.showing', {
                  from: indexOfFirstSpot + 1,
                  to: Math.min(indexOfLastSpot, filteredSpots.length),
                  total: filteredSpots.length
                })}
              </div>
              
              <div className="flex items-center space-x-1 md:space-x-2">
                <button 
                  onClick={prevPage} 
                  disabled={currentPage === 1}
                  className={`p-1 rounded-md ${currentPage === 1 
                    ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  aria-label={t('management.parking.pagination.previous')}
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                
                {pageNumbers.map(number => (
                  <button
                    key={number}
                    onClick={() => goToPage(number)}
                    className={`px-2 sm:px-3 py-1 rounded-md text-xs sm:text-sm ${
                      currentPage === number 
                        ? 'bg-primary text-white' 
                        : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'
                    }`}
                  >
                    {number}
                  </button>
                ))}
                
                <button 
                  onClick={nextPage} 
                  disabled={currentPage === totalPages}
                  className={`p-1 rounded-md ${currentPage === totalPages 
                    ? 'text-neutral-300 dark:text-neutral-600 cursor-not-allowed' 
                    : 'text-neutral-700 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800'}`}
                  aria-label={t('management.parking.pagination.next')}
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Modal for adding/editing spots */}
        <ParkingFormModal
          isOpen={isModalOpen}
          onClose={closeModal}
          onSubmit={handleSubmitSpot}
          spot={editingSpot}
          users={users}
          allSpots={spots}
        />

        {/* Delete confirmation modal */}
        <DeleteConfirmModal 
          isOpen={deleteModalOpen} 
          onClose={() => setDeleteModalOpen(false)} 
          onConfirm={confirmDelete} 
        />

        {/* Alert component */}
        {alertInfo.show && (
          <div className="fixed bottom-4 right-4 max-w-md z-50">
            <Alert 
              variant={alertInfo.variant}
              className="animate-in slide-in-from-right duration-300 shadow-lg"
            >
              <AlertTitle>
                {alertInfo.variant === 'success' 
                  ? t('management.parking.alerts.success') 
                  : alertInfo.variant === 'destructive' 
                    ? t('management.parking.alerts.error') 
                    : alertInfo.variant === 'warning' 
                      ? t('management.parking.alerts.warning') 
                      : t('management.parking.alerts.info')
                }
              </AlertTitle>
              <AlertDescription>
                {alertInfo.message}
              </AlertDescription>
              <button 
                onClick={hideAlert}
                className="absolute top-4 right-4 text-neutral-500 hover:text-neutral-800 dark:text-neutral-400 dark:hover:text-neutral-200"
                aria-label="Close alert"
              >
                ×
              </button>
            </Alert>
          </div>
        )}
      </div>
    );
};

export default ParkingManagement;